import { UnfurlItem } from '@lib/external/unfurl';
import { createFileFingerprintWithWorker } from '@lib/files/fingerprint';
import {
  filterOutDuplicates,
  generateHashesForItems,
  isMediaDuplicate,
} from '@lib/utils/chooseMediaUtils';

/**
 * Base interface for items that can be checked for duplicates
 * Both ExternalMediaItem and UploadedMediaItem should have a uniqueId property
 */
export interface DuplicateCheckableItem {
  uniqueId?: string;
}

/**
 * External media item (YouTube, Vimeo, etc.) with URL and metadata
 */
export interface ExternalMediaItem extends DuplicateCheckableItem {
  type: 'external';
  url: string;
  name: string;
  userMetadata?: Record<string, any>;
}

/**
 * Uploaded media item (user-uploaded files)
 */
export interface UploadedMediaItem extends DuplicateCheckableItem {
  type: 'uploaded';
  file?: File;
  name?: string;
  [key: string]: any;
}

/**
 * Result of duplicate detection operation
 */
export interface DuplicateResult {
  duplicates: (ExternalMediaItem | UploadedMediaItem)[];
  nonDuplicates: (ExternalMediaItem | UploadedMediaItem)[];
  duplicateCount: number;
  nonDuplicateCount: number;
}

/**
 * Options for hash generation during duplicate detection
 */
export interface HashGenerationOptions {
  /**
   * Existing external media items with hashes already generated
   * Used to match items and get existing hashes instead of generating new ones
   */
  existingExternalMediaWithHashes?: UnfurlItem[];
  /**
   * Whether to automatically generate hashes for items without uniqueId
   * @default false
   */
  autoGenerateHashes?: boolean;
}

/**
 * Service for generating hashes and detecting/filtering duplicate media items
 * Migrated from UploadURLStore.filterNonDuplicateVideoItems() and ChooseMediaModal duplicate detection
 */
export class DuplicateDetectionService {
  /**
   * Generate hashes for external media items (YouTube, Vimeo, etc.)
   * If existingExternalMediaWithHashes is provided, matches items to get existing hashes
   * Otherwise, generates new hashes from URL or name
   * @param items - Array of external media items to generate hashes for
   * @param existingExternalMediaWithHashes - Optional array of existing items with hashes for matching
   * @returns Promise resolving to items with uniqueId populated
   */
  async generateHashesForExternalMedia(
    items: ExternalMediaItem[],
    existingExternalMediaWithHashes?: UnfurlItem[],
  ): Promise<ExternalMediaItem[]> {
    if (items.length === 0) {
      return [];
    }

    // Build a Map of URL -> uniqueId from existing items for O(1) lookup
    // This allows us to reuse hashes that were already computed
    const existingHashesByUrl = new Map<string, string>();
    if (existingExternalMediaWithHashes && existingExternalMediaWithHashes.length > 0) {
      for (const existing of existingExternalMediaWithHashes) {
        if (existing.url && existing.uniqueId) {
          existingHashesByUrl.set(existing.url, existing.uniqueId);
        }
      }
    }

    return Promise.all(
      items.map(async (item) => {
        // Skip if already has uniqueId
        if (item.uniqueId) {
          return item;
        }

        // Check if we already have a hash for this URL
        // Since hashes are deterministic (same URL -> same hash), we can reuse it
        const existingUniqueId = item.url ? existingHashesByUrl.get(item.url) : undefined;
        if (existingUniqueId) {
          return {
            ...item,
            uniqueId: existingUniqueId,
          };
        }

        // Generate new hash from URL or name
        // Convert to UnfurlItem format for generateHashesForItems
        // Note: The 'id' field is a frontend-only identifier used for:
        // - UI selection tracking (selectedIds array)
        // - Grid row identification
        // - Input to generateUniqueId() to create the uniqueId hash
        // This is NOT the backend media _id (which is assigned by the backend after upload)
        const chooseMediaItem: UnfurlItem = {
          id: item.url || item.name,
          title: item.name,
          url: item.url,
          size: 0,
          type: 'video',
          userMetadata: item.userMetadata,
          // Required UnfurlItem properties
          columns: {},
          columnDefinitions: [],
        };

        const [enhanced] = await generateHashesForItems([chooseMediaItem]);
        return {
          ...item,
          uniqueId: enhanced.uniqueId,
        };
      }),
    );
  }

  /**
   * Generate hashes for uploaded media items (user-uploaded files)
   * Uses file fingerprint as uniqueId
   * @param items - Array of uploaded media items to generate hashes for
   * @returns Promise resolving to items with uniqueId populated
   */
  async generateHashesForUploadedMedia(items: UploadedMediaItem[]): Promise<UploadedMediaItem[]> {
    if (items.length === 0) {
      return [];
    }

    return Promise.all(
      items.map(async (item) => {
        // Skip if already has uniqueId
        if (item.uniqueId) {
          return item;
        }

        // Generate fingerprint for file
        if (!item.file) {
          // No file to generate hash from, return as-is
          return item;
        }

        try {
          const fingerprint = await createFileFingerprintWithWorker(item.file);
          return {
            ...item,
            uniqueId: fingerprint,
          };
        } catch (error) {
          console.error('Failed to generate hash for uploaded media item:', error);
          // Return item without uniqueId if hash generation fails
          return item;
        }
      }),
    );
  }

  /**
   * Generate hashes for mixed media items (external and uploaded)
   * @param items - Array of external or uploaded media items
   * @param existingExternalMediaWithHashes - Optional array of existing external media with hashes
   * @returns Promise resolving to items with uniqueId populated
   */
  async generateHashesForItems(
    items: (ExternalMediaItem | UploadedMediaItem)[],
    existingExternalMediaWithHashes?: UnfurlItem[],
  ): Promise<(ExternalMediaItem | UploadedMediaItem)[]> {
    if (items.length === 0) {
      return [];
    }

    // Separate items by type
    const externalMediaItems: ExternalMediaItem[] = [];
    const uploadedMediaItems: UploadedMediaItem[] = [];

    for (const item of items) {
      if (this.isExternalMediaItem(item)) {
        externalMediaItems.push(item);
      } else {
        uploadedMediaItems.push(item);
      }
    }

    // Generate hashes for each type in parallel
    const [hashedExternal, hashedUploaded] = await Promise.all([
      this.generateHashesForExternalMedia(externalMediaItems, existingExternalMediaWithHashes),
      this.generateHashesForUploadedMedia(uploadedMediaItems),
    ]);

    // Reconstruct in original order
    let externalIndex = 0;
    let uploadedIndex = 0;
    return items.map((item) => {
      if (this.isExternalMediaItem(item)) {
        return hashedExternal[externalIndex++];
      } else {
        return hashedUploaded[uploadedIndex++];
      }
    });
  }

  /**
   * Find duplicates in a list of items (async version with hash generation support)
   * @param items - Array of external or uploaded media items to check
   * @param existingUniqueIds - Record of existing unique IDs in the library
   * @param options - Optional hash generation options
   * @returns Promise resolving to DuplicateResult with duplicates and non-duplicates separated
   */
  async findDuplicates(
    items: (ExternalMediaItem | UploadedMediaItem)[],
    existingUniqueIds: Record<string, string>,
    options?: HashGenerationOptions,
  ): Promise<DuplicateResult> {
    // Generate hashes if needed
    let itemsWithHashes = items;
    if (options?.autoGenerateHashes) {
      itemsWithHashes = await this.generateHashesForItems(
        items,
        options.existingExternalMediaWithHashes,
      );
    }

    const duplicates: (ExternalMediaItem | UploadedMediaItem)[] = [];
    const nonDuplicates: (ExternalMediaItem | UploadedMediaItem)[] = [];

    for (const item of itemsWithHashes) {
      // isMediaDuplicate now accepts any item with uniqueId, no conversion needed
      if (isMediaDuplicate(item, existingUniqueIds)) {
        duplicates.push(item);
      } else {
        nonDuplicates.push(item);
      }
    }

    return {
      duplicates,
      nonDuplicates,
      duplicateCount: duplicates.length,
      nonDuplicateCount: nonDuplicates.length,
    };
  }

  /**
   * Filter out duplicates from a list of items (async version with hash generation support)
   * @param items - Array of external or uploaded media items to filter
   * @param existingUniqueIds - Record of existing unique IDs in the library
   * @param options - Optional hash generation options
   * @returns Promise resolving to array of non-duplicate items
   */
  async filterDuplicates(
    items: (ExternalMediaItem | UploadedMediaItem)[],
    existingUniqueIds: Record<string, string>,
    options?: HashGenerationOptions,
  ): Promise<(ExternalMediaItem | UploadedMediaItem)[]> {
    if (items.length === 0) {
      return [];
    }

    // Generate hashes if needed
    let itemsWithHashes = items;
    if (options?.autoGenerateHashes) {
      itemsWithHashes = await this.generateHashesForItems(
        items,
        options.existingExternalMediaWithHashes,
      );
    }

    // filterOutDuplicates now accepts any items with uniqueId, no conversion needed
    const filteredItems = filterOutDuplicates(itemsWithHashes, existingUniqueIds);

    // Map back to original format, preserving original items
    const filteredUniqueIds = new Set(filteredItems.map((item) => item.uniqueId).filter(Boolean));

    return itemsWithHashes.filter((item) => {
      if (!item.uniqueId) {
        // Items without uniqueId are kept (not considered duplicates)
        return true;
      }
      return filteredUniqueIds.has(item.uniqueId);
    });
  }

  /**
   * Type guard to check if an item is ExternalMediaItem
   * Uses the discriminator property for reliable type discrimination
   */
  private isExternalMediaItem(
    item: ExternalMediaItem | UploadedMediaItem,
  ): item is ExternalMediaItem {
    return item.type === 'external';
  }
}
